import { useCallback, useMemo, useState } from "react";
import i18next from "i18next";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Gallery from "../../../components/Gallery/Gallery";
import ModalMedia from "../../../components/Modal/ModalMedia";
import Button from "../../../components/Buttons/Button";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { startAddContentsToList } from "../../../actions/user";
import { getPathDatabaseByRole } from "../../../actions/auth";
import { useTemplatesModals } from "../../../contexts/templatesModalsContext";
import { useContentModals } from "../../../contexts/contentModalsContext";
import { getLanguageFormatted } from "../../../utils/string";
import {
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
} from "../../../utils/constants";
import {
  BUTTON_DESELECT_ALL,
  BUTTON_SELECT_ALL,
  MODAL_CREATE_WITH_IA_SAVE_TO_GALLERY,
  MODAL_CREATE_WITH_IA_SAVE_TO_GALLERY_NO_COUNT,
  MODAL_CREATE_WITH_IA_SAVED_SUCCESSFULLY,
  MODAL_CREATE_WITH_IA_SAVING_TO_GALLERY,
  TEMPLATES_PRODUCE_GENERATING,
  TEMPLATES_PRODUCE_RESULTS,
  TEMPLATES_PRODUCE_SAVE_ERROR,
} from "../../../i18n/keysTranslations";

const loadingBoxSx = {
  aspectRatio: "1",
  bgcolor: "grey.100",
  borderRadius: 2,
  border: 1,
  borderColor: "divider",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  gap: 1,
  width: "100%",
  minHeight: 180,
};

const getItemKey = (item) => String(item?.id || item?.url || "");

const ProduceResultsGallery = ({
  items = [],
  isGenerating = false,
  pendingCount = 0,
  onEditItem = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { closeTemplatesModals } = useTemplatesModals();
  const { openGallery } = useContentModals();
  const isModalGalleryOpen = useSelectorApp(
    (state) => state.ui?.openModals?.isModalGallery || false
  );
  const [previewUrl, setPreviewUrl] = useState("");
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const loadingItems = isGenerating ? Math.max(pendingCount, 0) : 0;
  const hasItems = items.length > 0;

  const selectableKeys = useMemo(
    () => items.map(getItemKey).filter(Boolean),
    [items]
  );
  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const selectedIndices = useMemo(
    () =>
      items.reduce((acc, item, index) => {
        const key = getItemKey(item);
        if (key && selectedKeySet.has(key)) acc.push(index);
        return acc;
      }, []),
    [items, selectedKeySet]
  );
  const selectedCount = selectedIndices.length;
  const allSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every((key) => selectedKeySet.has(key));

  const onSelectPreview = useCallback(
    (indexOrData) => {
      const index =
        typeof indexOrData === "object" && indexOrData !== null
          ? indexOrData.index
          : indexOrData;
      const url = items[index]?.url;
      if (url) setPreviewUrl(url);
    },
    [items]
  );

  const onClosePreview = useCallback(() => {
    setPreviewUrl("");
  }, []);

  const onSelectItem = useCallback(
    (index) => {
      const key = getItemKey(items[index]);
      if (!key) return;
      setSelectedKeys((prev) =>
        prev.includes(key)
          ? prev.filter((itemKey) => itemKey !== key)
          : [...prev, key]
      );
    },
    [items]
  );

  const onSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedKeys([]);
      return;
    }
    setSelectedKeys(selectableKeys);
  }, [allSelected, selectableKeys]);

  const onSaveToGallery = useCallback(async () => {
    if (selectedCount === 0 || isSaving) return;
    const itemsToSave = items
      .filter((item) => selectedKeySet.has(getItemKey(item)) && item.url)
      .map((item) => ({
        url: item.url,
        type: item.type || "upload",
        isAIGenerated: true,
      }));
    if (itemsToSave.length === 0) return;
    setIsSaving(true);
    try {
      const saveResult = await dispatch(
        startAddContentsToList(itemsToSave, { preserveOrder: true })
      );
      if (!saveResult) {
        SimpleAlert({
          title: t(TEMPLATES_PRODUCE_SAVE_ERROR),
          icon: "error",
        });
        return;
      }
      setSelectedKeys([]);
      SimpleAlert({
        title: t(MODAL_CREATE_WITH_IA_SAVED_SUCCESSFULLY),
        icon: "success",
      });
      closeTemplatesModals();
      if (!isModalGalleryOpen) {
        const { userID } = dispatch(getPathDatabaseByRole());
        openGallery({
          multiple: true,
          isOutsideCampaign: true,
          acceptedFormats: IMAGE_FORMATS.concat(VIDEO_FORMATS).concat(
            ADS_CAMPAIGN_CAROUSEL_FORMAT
          ),
          pathStorage: `socialMedia/${userID}/`,
          modeUpload: "sync",
          onCallbackSelectedItems: () => {},
          closeActionsDisabled: true,
          initialValues: {
            language: getLanguageFormatted(i18next.language),
          },
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    closeTemplatesModals,
    dispatch,
    isModalGalleryOpen,
    isSaving,
    items,
    openGallery,
    selectedCount,
    selectedKeySet,
    t,
  ]);

  if (!isGenerating && items.length === 0) return null;

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#fff",
        border: "1px solid #E4E4E7",
        borderRadius: "14px",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: "1.125rem" }}>
          {t(TEMPLATES_PRODUCE_RESULTS)}
        </Typography>
        {hasItems ? (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            <Button
              color="secondary"
              type="button"
              onClick={onSelectAll}
              disabled={isGenerating || !hasItems}
            >
              {allSelected ? t(BUTTON_DESELECT_ALL) : t(BUTTON_SELECT_ALL)}
            </Button>
            <Button
              color="primary"
              type="button"
              loading={isSaving}
              disabled={isSaving || selectedCount === 0}
              onClick={onSaveToGallery}
            >
              {isSaving
                ? t(MODAL_CREATE_WITH_IA_SAVING_TO_GALLERY)
                : selectedCount === 0
                  ? t(MODAL_CREATE_WITH_IA_SAVE_TO_GALLERY_NO_COUNT)
                  : t(MODAL_CREATE_WITH_IA_SAVE_TO_GALLERY, {
                      count: selectedCount,
                    })}
            </Button>
          </Box>
        ) : null}
      </Box>
      <Gallery
        data={items}
        columns={{ xs: 1, sm: 2, md: 3, lg: 4 }}
        showEmptyMessage={false}
        showDeleteButton={false}
        showPostButton={false}
        showActionButtons={true}
        showResizeFormatButton={false}
        showEditButton={false}
        showDimensionsOverlay={true}
        showPreviewExpandOnly={false}
        showExpandButton={true}
        showFooterEditButton={true}
        onFooterEdit={onEditItem}
        onSelectPreview={onSelectPreview}
        onSelectItem={onSelectItem}
        selectedItems={selectedIndices}
        multiple={true}
        acceptedFormats={IMAGE_FORMATS}
        loadLastItem={true}
        isLoading={isGenerating && loadingItems > 0}
        loadingItems={loadingItems}
        sxGallery={{ pl: 0, pr: 0 }}
        loadingComponent={{
          component: (
            <Box sx={loadingBoxSx}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                {t(TEMPLATES_PRODUCE_GENERATING)}
              </Typography>
            </Box>
          ),
        }}
      />
      {previewUrl ? (
        <ModalMedia
          modalOpen={Boolean(previewUrl)}
          onCloseModal={onClosePreview}
          url={previewUrl}
          showEditButton={false}
          showShareButton={false}
          showDeleteButton={false}
          isPicker={true}
        />
      ) : null}
    </Box>
  );
};

export default ProduceResultsGallery;
